import { memo, useState } from "react";
import { Ban, Clock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Spinner } from "@/shared/components/ui/spinner";
import { formatCurrency } from "@/shared/lib/formatters";
import { cn } from "@/shared/lib/utils";
import { useNow } from "../hooks/useNow";
import { getCancellationEligibility } from "../utils/cancellation";
import {
  NFCE_CANCELLATION_JUSTIFICATION_MAX_LENGTH,
  NFCE_CANCELLATION_JUSTIFICATION_MIN_LENGTH,
  NFCE_CANCELLATION_WINDOW_MINUTES,
} from "../constants";
import type { CommercialInvoiceResponseDTO } from "../types/commercial-invoice.types";

const WINDOW_TICK_MS = 15_000;

interface CommercialInvoiceCancelDialogProps {
  open: boolean;
  loading: boolean;
  invoice: CommercialInvoiceResponseDTO | null;
  onClose: () => void;
  onConfirm: (justification: string) => void;
}

export const CommercialInvoiceCancelDialog = memo(
  function CommercialInvoiceCancelDialog({
    open,
    loading,
    invoice,
    onClose,
    onConfirm,
  }: CommercialInvoiceCancelDialogProps) {
    const [justification, setJustification] = useState("");
    const now = useNow(open, WINDOW_TICK_MS);

    const eligibility = invoice
      ? getCancellationEligibility(invoice, now)
      : null;
    const withinWindow = eligibility?.allowed ?? false;

    const trimmedLength = justification.trim().length;
    const isTooShort =
      trimmedLength < NFCE_CANCELLATION_JUSTIFICATION_MIN_LENGTH;
    const isTooLong =
      trimmedLength > NFCE_CANCELLATION_JUSTIFICATION_MAX_LENGTH;
    const showLengthError = trimmedLength > 0 && (isTooShort || isTooLong);
    const canSubmit = withinWindow && !isTooShort && !isTooLong && !loading;

    const handleConfirm = () => {
      if (!canSubmit) return;
      onConfirm(justification.trim());
    };

    return (
      <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancelar nota comercial</DialogTitle>
            <DialogDescription>
              O cancelamento é registrado na SEFAZ e não pode ser desfeito.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-md border border-amber-500/20 bg-amber-500/10 p-4">
              <div className="flex items-start gap-3">
                <Ban className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-semibold">Nota #{invoice?.id}</p>
                  {invoice?.invoiceNumber && (
                    <p className="text-sm text-muted-foreground">
                      Nº {invoice.invoiceNumber}
                      {invoice.invoiceSeries
                        ? ` · série ${invoice.invoiceSeries}`
                        : ""}
                    </p>
                  )}
                  {invoice && (
                    <p className="text-sm text-muted-foreground">
                      Valor: {formatCurrency(invoice.value)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {withinWindow ? (
              <>
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4 shrink-0" />
                  Você tem cerca de {eligibility?.minutesLeft} min para
                  cancelar. O prazo é de {NFCE_CANCELLATION_WINDOW_MINUTES}{" "}
                  minutos após a autorização.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="nfce-cancel-justification">
                    Justificativa *
                  </Label>
                  <Textarea
                    id="nfce-cancel-justification"
                    rows={3}
                    placeholder="Ex.: Valor lançado incorretamente pelo operador"
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    disabled={loading}
                    aria-invalid={showLengthError}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={cn(
                        "text-xs",
                        showLengthError
                          ? "text-destructive"
                          : "text-muted-foreground",
                      )}
                    >
                      {isTooLong
                        ? `Máximo de ${NFCE_CANCELLATION_JUSTIFICATION_MAX_LENGTH} caracteres aceito pela SEFAZ`
                        : showLengthError
                          ? `Mínimo de ${NFCE_CANCELLATION_JUSTIFICATION_MIN_LENGTH} caracteres exigido pela SEFAZ`
                          : `De ${NFCE_CANCELLATION_JUSTIFICATION_MIN_LENGTH} a ${NFCE_CANCELLATION_JUSTIFICATION_MAX_LENGTH} caracteres`}
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                      {trimmedLength}/
                      {NFCE_CANCELLATION_JUSTIFICATION_MAX_LENGTH}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <p className="rounded-md border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
                {eligibility?.reason ?? "Esta nota não pode ser cancelada."}
              </p>
            )}
          </div>

          <DialogFooter className="mt-6 border-t pt-4">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Voltar
            </Button>
            {withinWindow && (
              <Button
                variant="destructive"
                onClick={handleConfirm}
                disabled={!canSubmit}
                className="gap-2"
              >
                {loading && <Spinner className="w-4 h-4" />}
                Cancelar nota
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);
